import {
  AlertTriangle,
  Columns2,
  Factory,
  FileUp,
  History,
  Keyboard,
  Layers,
  LineChart,
  Link2,
  Store,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCw,
  ShoppingCart,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface SpravaVypoctovMenuProps {
  /** Zákazka, ktorej histórii sa má odkaz venovať. */
  zakazkaId: string;
  /** Názov aktívneho výpočtu do popisku mazania; `null` = žiadny aktívny. */
  aktivnyNazov: string | null;
  /** Aktívna varianta je výrobná (mení znenie popiskov). */
  jeVyrobna: boolean;
  /** Zákazka už má výrobu — Nová výroba aj import pridajú ďalšiu časť. */
  maVyrobnu: boolean;
  /** Beží mazanie — položka Zmazať je zamknutá. */
  mazanieBezi: boolean;
  /** Počet výpočtov — pri jedinom nie je čo porovnávať. */
  pocetVypoctov: number;
  onPorovnat: () => void;
  onCenaHistoria: () => void;
  /**
   * „Vytvoriť sloty z materiálov" — pre zákazky z importu spred KOLA 96,
   * kde materiál sedí priamo na dielcoch. Voliteľné, staršie volajúce
   * ho nemajú.
   */
  onSlotyZMaterialov?: () => void;
  /** Beží vytváranie slotov — položka je zamknutá. */
  slotyBezia?: boolean;
  /**
   * „Aktualizovať z opraveného exportu" (KOLO 118) — user opravil chybu
   * v TopSolide a exportoval znova. Voliteľné.
   */
  onUpdateZExportu?: () => void;
  /**
   * Má výpočet uložený pôvod dielcov z TopSolidu? Bez neho sa riadky
   * exportu nedajú spárovať a položka je zamknutá s vysvetlením.
   */
  maPovodDielcov?: boolean;
  /** Nálezy z importu TopSolidu — voliteľné, staršie volajúce ho nemajú. */
  onOpravy?: () => void;
  /**
   * Nový výpočet. Voliteľné — bez práva na nábytok (obchodník, KOLO 231)
   * položka nie je, rovnako Import TopSolid a Zmazať.
   */
  onNovyVypocet?: () => void;
  /** Nový rozpočet tovaru — regály CRC vedľa nábytku (KOLO 230). Voliteľné. */
  onNovyTovar?: () => void;
  /**
   * Nová výrobná varianta (KOLO 208) — v zákazke, ktorá výrobu má, je to
   * ďalšia časť výroby. Voliteľné.
   */
  onNovaVyroba?: () => void;
  /** Premenovať otvorenú časť / výpočet (KOLO 203). Voliteľné. */
  onPremenovat?: () => void;
  /** Otvorená varianta je časť výroby — mení znenie popiskov. */
  jeCast?: boolean;
  /**
   * Pripojiť výrobu k výpočtu / k výpočtu (KOLO 208), spojiť s inou výrobou
   * (KOLO 209) — položky v poradí.
   */
  pripojit?: { label: string; title: string; onSelect: () => void }[];
  onImportTopSolid?: () => void;
  onSkratky: () => void;
  onZmazat?: () => void;
}

/**
 * Správa výpočtov — jedno „⋮" menu pri taboch.
 *
 * Predtým boli tieto akcie v toolbare vedľa seba a **kôš na zmazanie výpočtu
 * sedel priamo pri „+ Výpočet"** — najhoršia možná susednosť. Import TopSolid
 * bol navyše len ikona bez popisu (KOLO 54, aby sa toolbar nezalamoval).
 * Tu má každá položka text a mazanie je za dvomi krokmi.
 */
export function SpravaVypoctovMenu({
  zakazkaId,
  aktivnyNazov,
  jeVyrobna,
  maVyrobnu,
  mazanieBezi,
  pocetVypoctov,
  onPorovnat,
  onCenaHistoria,
  onSlotyZMaterialov,
  slotyBezia = false,
  onUpdateZExportu,
  maPovodDielcov = false,
  onOpravy,
  onNovyVypocet,
  onNovyTovar,
  onNovaVyroba,
  onPremenovat,
  jeCast = false,
  pripojit = [],
  onImportTopSolid,
  onSkratky,
  onZmazat,
}: SpravaVypoctovMenuProps): JSX.Element {
  const zmazatLabel = aktivnyNazov
    ? jeVyrobna
      ? `Zmazať výrobnú variantu ${aktivnyNazov}`
      : jeCast
        ? `Zmazať časť ${aktivnyNazov}`
        : `Zmazať výpočet ${aktivnyNazov}`
    : "Zmazať výpočet";
  const premenovatLabel = jeCast
    ? `Premenovať časť ${aktivnyNazov ?? ""}`.trim()
    : jeVyrobna
      ? `Premenovať výrobu ${aktivnyNazov ?? ""}`.trim()
      : `Premenovať výpočet ${aktivnyNazov ?? ""}`.trim();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 shrink-0"
          aria-label="Spravovať výpočty"
          title="Nový výpočet, import TopSolid, história zmien, skratky, zmazanie"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>Výpočty</DropdownMenuLabel>
        {onNovyVypocet ? (
          <DropdownMenuItem onSelect={onNovyVypocet}>
            <Plus className="h-4 w-4" />
            Nový výpočet
          </DropdownMenuItem>
        ) : null}
        {onNovaVyroba ? (
          <DropdownMenuItem
            onSelect={onNovaVyroba}
            title={
              maVyrobnu
                ? "Ďalšia časť výroby — na pílu ide samostatne, výroba spolu je súčet častí"
                : "Preklopenie výpočtu do výroby — výrobná kalkulácia"
            }
          >
            <Factory className="h-4 w-4" />
            {maVyrobnu ? "Nová časť výroby" : "Nová výrobná varianta"}
          </DropdownMenuItem>
        ) : null}
        {onNovyTovar ? (
          <DropdownMenuItem
            onSelect={onNovyTovar}
            title="Rozpočet tovaru (regály CRC) — cena sa pripočíta k nábytku, ponuka je jedna"
          >
            <Store className="h-4 w-4" />
            Nový tovar (regály)
          </DropdownMenuItem>
        ) : null}
        {pripojit.map((p) => (
          <DropdownMenuItem key={p.label} onSelect={p.onSelect} title={p.title}>
            <Link2 className="h-4 w-4" />
            {p.label}
          </DropdownMenuItem>
        ))}
        {onPremenovat ? (
          <DropdownMenuItem onSelect={onPremenovat}>
            <Pencil className="h-4 w-4" />
            {premenovatLabel}
          </DropdownMenuItem>
        ) : null}
        {onImportTopSolid ? (
          <DropdownMenuItem
            onSelect={onImportTopSolid}
            title={
              maVyrobnu
                ? "Import TopSolid CSV ako ďalšia časť výroby"
                : "Import TopSolid CSV ako výrobná varianta"
            }
          >
            <FileUp className="h-4 w-4" />
            Import TopSolid CSV
          </DropdownMenuItem>
        ) : null}

        {onUpdateZExportu ? (
          <DropdownMenuItem
            disabled={!maPovodDielcov}
            onSelect={onUpdateZExportu}
            title={
              maPovodDielcov
                ? "Chybu si opravil v TopSolide a exportoval znova? " +
                  `Prejdeš mapovaním a potom uvidíš, čo sa ${jeVyrobna ? "vo výrobe" : "vo výpočte"} ` +
                  "zmení, kým sa to stane. Ručné úpravy sa nechávajú."
                : `${jeVyrobna ? "Táto výroba" : "Tento výpočet"} nemá uložený pôvod dielcov z TopSolidu ` +
                  "(ukladá sa až od KOLA 117), takže sa riadky exportu " +
                  "nedajú spárovať. Použi nový import."
            }
          >
            <RefreshCw className="h-4 w-4" />
            Aktualizovať z opraveného exportu
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuItem
          disabled={pocetVypoctov < 2}
          onSelect={onPorovnat}
          title={
            pocetVypoctov < 2
              ? "Na porovnanie treba aspoň dva výpočty"
              : "Rozdielová tabuľka dvoch výpočtov — súčty, zostavy, dielce"
          }
        >
          <Columns2 className="h-4 w-4" />
          Porovnať výpočty
        </DropdownMenuItem>

        {onSlotyZMaterialov ? (
          <DropdownMenuItem
            disabled={slotyBezia}
            onSelect={onSlotyZMaterialov}
            title={
              "Z materiálov priradených priamo na dielcoch spraví materiály zákazky " +
              "M1, M2… — dekor sa potom dá vymeniť pre celú zákazku naraz. " +
              "Cena sa nemení."
            }
          >
            <Layers className="h-4 w-4" />
            Vytvoriť materiály zákazky (M1, M2…)
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            to={`/objednavky?zakazka_id=${zakazkaId}`}
            title="Čo treba na túto zákazku kúpiť a kto to už objednal"
          >
            <ShoppingCart className="h-4 w-4" />
            Objednávky zákazky
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            to={`/historia?zakazka_id=${zakazkaId}`}
            title="Kto, kedy a čo na tejto zákazke zmenil + odpracovaný čas"
          >
            <History className="h-4 w-4" />
            História zmien zákazky
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={onCenaHistoria}
          title="Za koľko bola zákazka ocenená pri každom prechode stavu"
        >
          <LineChart className="h-4 w-4" />
          História cien zákazky
        </DropdownMenuItem>
        {onOpravy ? (
          <DropdownMenuItem
            onSelect={onOpravy}
            title="Čo appka pri importe musela obísť — oprav to v modeli"
          >
            <AlertTriangle className="h-4 w-4" />
            Na opravu v TopSolide
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          onSelect={onSkratky}
          title="Prehľad klávesových skratiek editora (kláves ?)"
        >
          <Keyboard className="h-4 w-4" />
          Klávesové skratky
        </DropdownMenuItem>

        {onZmazat ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!aktivnyNazov || mazanieBezi}
              onSelect={onZmazat}
              className="text-destructive"
              title="Zmaže výpočet vrátane jeho zostáv a dielcov — pýta si potvrdenie"
            >
              <Trash2 className="h-4 w-4" />
              {zmazatLabel}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
